import React from 'react';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import {
  notEmptyString,
} from '@populateui/utils';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import startCase from 'lodash/startCase';
import MetricCard from 'core/components/Charts/MetricCard';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import {
  RTMPatientTrendResponseDTO,
  RTMReportedBarrierDTO,
  RTMUserDTO,
} from 'dtos';
import {
  RTMMonitoringPriority,
} from 'dtos/rTMMonitoringPriority';
import Button from 'pages/Dashboard/components/Button';
import Name from 'pages/Dashboard/components/Name';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  getPriorityAccentClass,
  getPriorityValueClass,
  PRIORITY_BADGE_CLASSES,
} from 'pages/Dashboard/pages/Dashboard/RTM/Trends/constants';
import {
  getPatientTrendSummaryViewModel,
  TrendChartRow,
} from 'pages/Dashboard/pages/Dashboard/RTM/Trends/helpers';
import {
  getDisplayName,
  getInitials,
} from 'pages/Dashboard/utils/helper';
import {
  formatDate,
} from 'utils/date';
import dayjs from 'utils/dayjs';

type Props = {
  patientTrend?: RTMPatientTrendResponseDTO;
  chartRows: TrendChartRow[];
  isMarkReviewedPending: boolean;
  onMarkReviewed: () => void;
  reviewedByUser?: RTMUserDTO;
};

type PriorityBadgeProps = {
  priority?: RTMMonitoringPriority;
};

type ReportedBarriersSectionProps = {
  barriers: RTMReportedBarrierDTO[];
};

type UrgentAlertCardProps = {
  title: string;
  tags?: string[] | null;
};

type MetadataBarProps = {
  lastCheckinDate?: string | null;
  reportsInPeriod: number;
  dateRangeLabel: string;
  hasUnreviewedCheckIns?: boolean;
  unreviewedCheckInCount: number;
  lastReviewedOn?: string | null;
  reviewedByUser?: RTMUserDTO;
  isMarkReviewedPending: boolean;
  onMarkReviewed: () => void;
};

function getDeltaArrow(delta: number): string {
  if (delta > 0) {
    return '↑';
  }

  return delta < 0 ? '↓' : '';
}

function PriorityBadge({ priority }: PriorityBadgeProps) {
  return isNil(priority) ? (
    <span>{DEFAULT_EMPTY_VALUE}</span>
  ) : (
    <span
      className={clsx(
        'inline-flex rounded-full px-2.5 py-0.5 text-base font-semibold whitespace-nowrap',
        PRIORITY_BADGE_CLASSES[priority],
      )}
    >
      {startCase(priority)}
    </span>
  );
}

function ReportedBarriersSection({ barriers }: ReportedBarriersSectionProps) {
  return (
    <div className="rtm-barriers-section flex flex-col gap-1 w-fit min-w-[calc(25%-16px)]">
      <div className="text-xs font-medium text-gray-600">
        Reported barriers to adherence
      </div>
      {barriers.length === 0 ? (
        <span className="inline-flex w-fit rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
          No barriers reported
        </span>
      ) : (
        <ul className="list-disc space-y-1.5 rounded-xl bg-amber-50 border border-amber-200 px-5 py-3 text-amber-950 marker:text-amber-600">
          {barriers.map((barrier) => (
            <li
              key={barrier.value ?? barrier.label ?? ''}
              className="pl-0.5"
            >
              <div className="text-xs font-semibold">
                {barrier.label ?? DEFAULT_EMPTY_VALUE}
              </div>
              {notEmptyString(barrier.clinicalHint) ? (
                <div className="mt-0.5 font-medium text-[11px] text-amber-900/70">
                  {barrier.clinicalHint}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function UrgentAlertCard({ title, tags }: UrgentAlertCardProps) {
  const visibleTags = (tags ?? []).filter(notEmptyString);

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
      <div className="text-sm font-semibold text-red-950">
        {title}
      </div>
      {visibleTags.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {visibleTags.map((tag) => (
            <span
              key={tag}
              className="inline-flex rounded-lg bg-red-100 px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap text-red-800"
            >
              {tag}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function MetadataBar({
  lastCheckinDate,
  reportsInPeriod,
  dateRangeLabel,
  hasUnreviewedCheckIns,
  unreviewedCheckInCount,
  lastReviewedOn,
  reviewedByUser,
  isMarkReviewedPending,
  onMarkReviewed,
}: MetadataBarProps) {
  const items = [
    {
      label: 'Last check-in',
      value: formatDate(lastCheckinDate ?? '') || DEFAULT_EMPTY_VALUE,
      Icon: EventAvailableOutlinedIcon,
    },
    {
      label: 'Reports in period',
      value: String(reportsInPeriod),
      Icon: AssessmentOutlinedIcon,
    },
    {
      label: 'Date range',
      value: dateRangeLabel,
      Icon: DateRangeOutlinedIcon,
    },
  ];

  return (
    <div className="rtm-metadata-bar flex items-stretch divide-x divide-gray-200 text-xs text-gray-700 border border-gray-200 rounded-lg p-4 shadow">
      {items.map((item, itemIndex) => (
        <div
          key={item.label}
          className={clsx(
            'rtm-metadata-item flex flex-1 min-w-0 items-center gap-2',
            { 'pl-4': itemIndex > 0 },
          )}
        >
          <span className="inline-flex items-center justify-center rounded-full bg-gray-100 p-2">
            <item.Icon className="!h-5 !w-5 text-gray-700" />
          </span>
          <div className="flex flex-col gap-1 min-w-0">
            <span className="text-gray-600 font-medium">{item.label}</span>
            <span className="font-semibold truncate">{item.value}</span>
          </div>
        </div>
      ))}
      <div className="rtm-metadata-item flex flex-1 min-w-0 items-center gap-2 pl-4">
        <span className="inline-flex items-center justify-center rounded-full bg-gray-100 p-2">
          {hasUnreviewedCheckIns ? (
            <CheckCircleOutlinedIcon className="!h-5 !w-5 text-gray-700" />
          ) : (
            <CheckCircleRoundedIcon className="!h-5 !w-5 !text-populate-green-1" />
          )}
        </span>
        {hasUnreviewedCheckIns ? (
          <div className="flex flex-col gap-1">
            <span className="text-gray-600 font-medium text-xs">
              <span className="text-gray-800 font-semibold">{unreviewedCheckInCount}</span>
              {` Unreviewed check-in${unreviewedCheckInCount === 1 ? '' : 's'}`}
            </span>
            <Button
              type="button"
              variant="outlined"
              color="primary"
              size="small"
              className={clsx("!px-2 !py-0.5 !text-xs flex items-center gap-1 !border-blue-500", {
                '!border-gray-200 !text-gray-500': isMarkReviewedPending,
                '!border-blue-500 !text-blue-500': !isMarkReviewedPending,
              })}
              disabled={isMarkReviewedPending}
              onClick={onMarkReviewed}
            >
              {isMarkReviewedPending
                ? <Spinner size={12} />
                : <CheckCircleOutlinedIcon className="!h-4 !w-4" />}
              Mark as Reviewed
            </Button>
          </div>
        ) : (
          <div className="min-w-0 flex-1 flex flex-col gap-1 text-xs text-gray-500 font-medium">
            <Name
              name={getDisplayName(reviewedByUser?.formattedNames, DEFAULT_EMPTY_VALUE)}
              initials={getInitials(reviewedByUser?.formattedNames)}
              image={{ size: 'xs' }}
              className="min-w-0"
              labelClassName="text-xs font-semibold text-gray-700"
            >
              {formatDate(lastReviewedOn ?? '')}
            </Name>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PatientTrendSummary({
  patientTrend,
  chartRows,
  isMarkReviewedPending,
  onMarkReviewed,
  reviewedByUser,
}: Props) {
  const {
    lastCheckIn,
    barriers,
    metadata,
    conditionLabel,
    adherenceLabel,
    conditionDescription,
    adherenceDescription,
  } = getPatientTrendSummaryViewModel(patientTrend, chartRows);

  const unreviewedCheckInCount = (patientTrend?.points ?? []).filter(
    (point) => point.isReviewed === false,
  ).length;
  const urgentAlert = patientTrend?.urgentAlert;
  const { title: urgentAlertTitle, tags: urgentAlertTags } = urgentAlert ?? {};

  return (
    <div className="flex flex-col gap-5">
      <MetadataBar
        lastCheckinDate={metadata.lastCheckinDate}
        reportsInPeriod={metadata.reportsInPeriod}
        dateRangeLabel={metadata.dateRangeLabel}
        hasUnreviewedCheckIns={patientTrend?.hasUnreviewedCheckIns}
        unreviewedCheckInCount={unreviewedCheckInCount}
        lastReviewedOn={patientTrend?.lastReviewedOn}
        reviewedByUser={reviewedByUser}
        isMarkReviewedPending={isMarkReviewedPending}
        onMarkReviewed={onMarkReviewed}
      />

      {notEmptyString(urgentAlertTitle) ? (
        <UrgentAlertCard
          title={urgentAlertTitle ?? ''}
          tags={urgentAlertTags ?? []}
        />
      ) : null}

      <div className="flex flex-col">
        <div className="text-lg font-semibold text-gray-800">
          Check-in Overview
        </div>
        <div className="rtm-metric-cards mt-1 grid grid-cols-4 gap-6">
          <MetricCard
            header="Status"
            className={getPriorityAccentClass(patientTrend?.priority)}
            value={<PriorityBadge priority={patientTrend?.priority} />}
            auxiliaryData={notEmptyString(patientTrend?.reason) ? (
              <span className="font-medium text-gray-500 whitespace-normal break-words">
                {patientTrend?.reason}
              </span>
            ) : undefined}
          />
          <MetricCard
            header="Pain — Q1 PEG-3"
            className={getPriorityAccentClass(lastCheckIn?.painSeverityStatus)}
            value={isNil(lastCheckIn?.peg3Composite) ? (
              DEFAULT_EMPTY_VALUE
            ) : (
              <>
                {lastCheckIn.peg3Composite}
                <span className="text-xs text-gray-500">/10</span>
              </>
            )}
            auxiliaryData={(
              <div className="flex flex-col gap-0.5">
                {(notEmptyString(lastCheckIn?.painSeverityLabel)
                  || !isNil(lastCheckIn?.priorPeg3Composite)) ? (
                    <span className="text-gray-600">
                      {[
                        lastCheckIn?.painSeverityLabel,
                        !isNil(lastCheckIn?.priorPeg3Composite)
                          ? `prior ${lastCheckIn.priorPeg3Composite}`
                          : undefined,
                      ].filter(notEmptyString).join(' · ')}
                    </span>
                  ) : null}
                {!isNil(lastCheckIn?.peg3Delta) ? (
                  <span
                    className={clsx(
                      'font-medium',
                      lastCheckIn.peg3Delta === 0
                        ? 'text-gray-500'
                        : getPriorityValueClass(lastCheckIn.painSeverityStatus),
                    )}
                  >
                    {`${getDeltaArrow(lastCheckIn.peg3Delta)}${Math.abs(lastCheckIn.peg3Delta)} pts from prior`}
                  </span>
                ) : null}
                {!isNil(lastCheckIn?.consecutiveWorseningCount)
                  && lastCheckIn.consecutiveWorseningCount > 0 ? (
                    <span className="font-medium text-red-600">
                      {`↑ worsening ${lastCheckIn.consecutiveWorseningCount} check-ins in a row`}
                    </span>
                  ) : null}
                {!isNil(lastCheckIn?.largestJump?.delta) ? (
                  <span className="text-gray-500">
                    {`Largest single jump: ${lastCheckIn.largestJump.delta} pts${
                      notEmptyString(lastCheckIn.largestJump.completedOn)
                        ? ` (${dayjs(lastCheckIn.largestJump.completedOn).format('MMM D')})`
                        : ''
                    }`}
                  </span>
                ) : null}
              </div>
            )}
          />

          <MetricCard
            header="Condition — Q2"
            className={getPriorityAccentClass(lastCheckIn?.conditionChangeStatus)}
            value={conditionLabel ? (
              <span>
                {conditionLabel}
              </span>
            ) : DEFAULT_EMPTY_VALUE}
            auxiliaryData={conditionDescription}
          />

          <MetricCard
            header="Adherence — Q3"
            className={getPriorityAccentClass(lastCheckIn?.carePlanAdherenceStatus)}
            value={adherenceLabel ? (
              <span>
                {adherenceLabel}
              </span>
            ) : DEFAULT_EMPTY_VALUE}
            auxiliaryData={adherenceDescription}
          />
        </div>
      </div>

      <ReportedBarriersSection barriers={barriers} />
    </div>
  );
}
